import * as THREE from 'three';
import * as CopcLib from 'copc';
import { PointCloudTreeNode } from '../../pointcloud-utils/point-cloud-tree.js';
import {
  PointAttributes,
  PointAttribute,
  PointAttributeTypes,
} from '../../types/PointAttributes.js';
import {
  workerPool,
  getSasToken,
  getNumNodesLoading,
  getMaxNodesLoading,
  incrementNodesLoading,
  decrementNodesLoading,
} from '../config.service';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EptJson {
  bounds: number[];
  boundsConforming: number[];
  span: number;
  schema: any[];
  dataType: 'laszip' | 'binary' | 'zstandard';
  srs?: { authority?: string; horizontal?: string; wkt?: string };
  scale?: number[];
  offset?: number[];
}

// ─── Worker factories ─────────────────────────────────────────────────────────

function createEptBinaryDecoderWorker(): Worker {
  return new Worker(new URL('../../workers/ept/EptBinaryDecoderWorker.ts', import.meta.url));
}

function createEptLaszipDecoderWorker(): Worker {
  return new Worker(new URL('../../workers/ept/EptLaszipDecoderWorker.ts', import.meta.url), {
    type: 'module',
  });
}

function createEptZstandardDecoderWorker(): Worker {
  return new Worker(new URL('../../workers/ept/EptZstandardDecoderWorker.ts', import.meta.url));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function appendSasToken(url: string): string {
  const token = getSasToken();
  if (!token) return url;
  const hashIndex = url.indexOf('#');
  const path = hashIndex < 0 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex < 0 ? '' : url.slice(hashIndex);
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${token}${fragment}`;
}

class Version {
  major: number;
  minor: number;
  patch: number;
  version: string;

  constructor(versionString: string) {
    const parts = versionString.split('.');
    this.major = parseInt(parts[0]) || 0;
    this.minor = parseInt(parts[1]) || 0;
    this.patch = parseInt(parts[2]) || 0;
    this.version = versionString;
  }

  toString(): string {
    return this.version;
  }
}

class U {
  static toVector3(v: number[], offset?: number): THREE.Vector3 {
    return new THREE.Vector3().fromArray(v, offset || 0);
  }

  static toBox3(b: number[]): THREE.Box3 {
    return new THREE.Box3(U.toVector3(b), U.toVector3(b, 3));
  }

  static sphereFrom(b: THREE.Box3): THREE.Sphere {
    return b.getBoundingSphere(new THREE.Sphere());
  }

  static toPotreeName([d, x, y, z]: number[]): string {
    let name = 'r';
    for (let i = 0; i < d; ++i) {
      const shift = d - i - 1;
      const mask = 1 << shift;
      let step = 0;
      if (x & mask) step += 4;
      if (y & mask) step += 2;
      if (z & mask) step += 1;
      name += step;
    }
    return name;
  }
}

// ─── Data Loaders ─────────────────────────────────────────────────────────────

export class EptBinaryLoader {
  extension(): string {
    return '.bin';
  }

  workerKey(): string {
    return 'EptBinaryDecoder';
  }

  workerFactory(): () => Worker {
    return createEptBinaryDecoderWorker;
  }

  async load(node: PointCloudEptGeometryNode): Promise<void> {
    if (node.loaded) return;

    const { Key } = CopcLib;
    const url = `${(node.owner as any).base}/ept-data/${Key.toString(node.key)}${this.extension()}`;

    try {
      const response = await fetch(appendSasToken(url));
      if (!response.ok) {
        console.log('Failed ' + url + ': ' + response.status);
        return;
      }
      const buffer = await response.arrayBuffer();
      this.parse(node, buffer);
    } catch (e) {
      console.log('Failed request: ' + e);
    }
  }

  parse(node: PointCloudEptGeometryNode, buffer: ArrayBuffer): void {
    const workerKey = this.workerKey();
    const worker = workerPool.getWorker(workerKey, this.workerFactory());

    worker.onmessage = (e: MessageEvent) => {
      const g = new THREE.BufferGeometry();
      const numPoints: number = e.data.numPoints;

      const position = new Float32Array(e.data.position);
      g.setAttribute('position', new THREE.BufferAttribute(position, 3));

      const indices = new Uint8Array(e.data.indices);
      g.setAttribute('indices', new THREE.BufferAttribute(indices, 4));

      if (e.data.color) {
        const color = new Uint8Array(e.data.color);
        g.setAttribute('rgba', new THREE.BufferAttribute(color, 4, true));
      }
      if (e.data.intensity) {
        const intensity = new Float32Array(e.data.intensity);
        g.setAttribute('intensity', new THREE.BufferAttribute(intensity, 1));
      }
      if (e.data.classification) {
        const classification = new Uint8Array(e.data.classification);
        g.setAttribute('classification', new THREE.BufferAttribute(classification, 1));
      }
      if (e.data.returnNumber) {
        const returnNumber = new Uint8Array(e.data.returnNumber);
        g.setAttribute('return number', new THREE.BufferAttribute(returnNumber, 1));
      }
      if (e.data.numberOfReturns) {
        const numberOfReturns = new Uint8Array(e.data.numberOfReturns);
        g.setAttribute('number of returns', new THREE.BufferAttribute(numberOfReturns, 1));
      }
      if (e.data.pointSourceId) {
        const pointSourceId = new Uint16Array(e.data.pointSourceId);
        g.setAttribute('source id', new THREE.BufferAttribute(pointSourceId, 1));
      }

      g.attributes['indices'].normalized = true;

      const tightBoundingBox = new THREE.Box3(
        new THREE.Vector3().fromArray(e.data.tightBoundingBox.min),
        new THREE.Vector3().fromArray(e.data.tightBoundingBox.max),
      );

      node.doneLoading(g, tightBoundingBox, numPoints, new THREE.Vector3(...e.data.mean));
      workerPool.returnWorker(workerKey, worker);
    };

    const ept = (node.owner as any).ept;
    const schema = ept.schema;
    const scale = ept.scale || [1, 1, 1];
    const offset = ept.offset || [0, 0, 0];

    const { Bounds } = CopcLib;
    const mins = Bounds.min(node.bounds as any);

    const message = {
      buffer,
      schema,
      scale,
      offset,
      mins,
    };

    worker.postMessage(message, [message.buffer]);
  }
}

export class EptZstandardLoader extends EptBinaryLoader {
  override extension(): string {
    return '.zst';
  }

  override workerKey(): string {
    return 'EptZstandardDecoder';
  }

  override workerFactory(): () => Worker {
    return createEptZstandardDecoderWorker;
  }
}

export class EptLaszipLoader {
  async load(node: PointCloudEptGeometryNode): Promise<void> {
    if (node.loaded) return;

    const { Key } = CopcLib;
    const url = `${(node.owner as any).base}/ept-data/${Key.toString(node.key)}.laz`;
    const response = await fetch(appendSasToken(url));
    const buffer = await response.arrayBuffer();
    this.parse(node, buffer);
  }

  async parse(node: PointCloudEptGeometryNode, compressed: ArrayBuffer): Promise<void> {
    const handler = new EptLazBatcher(node);

    try {
      const { Bounds, Las } = CopcLib;
      const get = (begin: number, end: number) =>
        Promise.resolve(new Uint8Array(compressed, begin, end - begin));

      const header = Las.Header.parse(new Uint8Array(compressed));
      const vlrs = await Las.Vlr.walk(get as any, header);
      let eb: any[] = [];
      const ebVlr = Las.Vlr.find(vlrs, 'LASF_Spec', 4);
      if (ebVlr) eb = Las.ExtraBytes.parse(await Las.Vlr.fetch(get as any, ebVlr));

      const message = {
        isFullFile: true,
        compressed,
        header,
        eb,
        pointCount: header.pointCount,
        nodemin: Bounds.min(node.bounds as any),
      };
      handler.push(message);
    } catch (e) {
      console.log('Failed:', e);
    }
  }
}

export class CopcLaszipLoader {
  async load(node: PointCloudEptGeometryNode): Promise<void> {
    if (node.loaded) return;

    const { pointCount, pointDataOffset, pointDataLength } = node.nodeinfo;

    if (!pointCount) return this.parse(node, new ArrayBuffer(0));
    const compressed = await (node.owner as any).getter(
      pointDataOffset,
      pointDataOffset + pointDataLength,
    );
    this.parse(node, compressed.buffer);
  }

  parse(node: PointCloudEptGeometryNode, compressed: ArrayBuffer): void {
    const handler = new EptLazBatcher(node);

    try {
      handler.push({
        isFullFile: false,
        compressed,
        header: (node.owner as any).copc.header,
        eb: (node.owner as any).copc.eb,
        pointCount: node.nodeinfo.pointCount,
        nodemin: node.bounds.slice(0, 3),
      });
    } catch (e) {
      console.log('Failed:', e);
    }
  }
}

class EptLazBatcher {
  private node: PointCloudEptGeometryNode;

  constructor(node: PointCloudEptGeometryNode) {
    this.node = node;
  }

  push(las: {
    isFullFile: boolean;
    compressed: ArrayBuffer;
    header: any;
    eb: any[];
    pointCount: number;
    nodemin: number[];
  }): void {
    const { isFullFile, compressed, header, eb, pointCount, nodemin } = las;

    const workerKey = 'EptLaszipDecoder';
    const worker = workerPool.getWorker(workerKey, createEptLaszipDecoderWorker);
    const pointAttributes = this.node.owner.pointAttributes;

    worker.onmessage = (e: MessageEvent) => {
      const g = new THREE.BufferGeometry();

      const positions = new Float32Array(e.data.position);
      const colors = new Uint8Array(e.data.color);
      const intensities = new Float32Array(e.data.intensity);
      const classifications = new Uint8Array(e.data.classification);
      const returnNumbers = new Uint8Array(e.data.returnNumber);
      const numberOfReturns = new Uint8Array(e.data.numberOfReturns);
      const pointSourceIds = new Uint16Array(e.data.pointSourceId);
      const indices = new Uint8Array(e.data.indices);
      const gpsTime = new Float32Array(e.data.gpsTime);

      g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      g.setAttribute('rgba', new THREE.BufferAttribute(colors, 4, true));
      g.setAttribute('intensity', new THREE.BufferAttribute(intensities, 1));
      g.setAttribute('classification', new THREE.BufferAttribute(classifications, 1));
      g.setAttribute('return number', new THREE.BufferAttribute(returnNumbers, 1));
      g.setAttribute('number of returns', new THREE.BufferAttribute(numberOfReturns, 1));
      g.setAttribute('source id', new THREE.BufferAttribute(pointSourceIds, 1));
      g.setAttribute('indices', new THREE.BufferAttribute(indices, 4));
      g.setAttribute('gps-time', new THREE.BufferAttribute(gpsTime, 1));
      (this.node as any).gpsTime = e.data.gpsMeta;

      g.attributes['indices'].normalized = true;

      for (const key in e.data.ranges) {
        const range = e.data.ranges[key];
        const attribute = pointAttributes.attributes.find((a: any) => a.name === key);
        if (attribute) {
          attribute.range[0] = Math.min(attribute.range[0], range[0]);
          attribute.range[1] = Math.max(attribute.range[1], range[1]);
          if (!attribute.initialRange) {
            attribute.initialRange = attribute.range;
          }
        }
      }

      const tightBoundingBox = new THREE.Box3(
        new THREE.Vector3().fromArray(e.data.tightBoundingBox.min),
        new THREE.Vector3().fromArray(e.data.tightBoundingBox.max),
      );

      this.node.doneLoading(g, tightBoundingBox, pointCount, new THREE.Vector3(...e.data.mean));

      workerPool.returnWorker(workerKey, worker);
    };

    const message = { isFullFile, compressed, header, eb, pointCount, nodemin };
    worker.postMessage(message, [message.compressed]);
  }
}

// ─── Geometry Classes ─────────────────────────────────────────────────────────

class BaseGeometry {
  cube: number[];
  boundingBox: THREE.Box3;
  tightBoundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  tightBoundingSphere: THREE.Sphere;
  offset: THREE.Vector3;
  version: Version;
  loader!: EptBinaryLoader | EptLaszipLoader | EptZstandardLoader | CopcLaszipLoader;
  spacing: number;
  projection: string | null;
  pointAttributes: any;
  root!: PointCloudEptGeometryNode;

  constructor({
    cube,
    boundsConforming,
    spacing,
    srs,
  }: {
    cube: number[];
    boundsConforming: number[];
    spacing: number;
    srs?: string | null;
  }) {
    this.cube = cube;
    this.boundingBox = U.toBox3(cube);
    this.tightBoundingBox = U.toBox3(boundsConforming);
    this.boundingSphere = U.sphereFrom(this.boundingBox);
    this.tightBoundingSphere = U.sphereFrom(this.tightBoundingBox);
    this.offset = U.toVector3([0, 0, 0]);
    this.version = new Version('1.7');
    this.loader = new CopcLaszipLoader();
    this.spacing = spacing;
    this.projection = srs || null;

    const attributes = new (PointAttributes as any)();
    attributes.add(PointAttribute.POSITION_CARTESIAN);
    attributes.add(new (PointAttribute as any)('rgba', PointAttributeTypes.DATA_TYPE_UINT8, 4));
    attributes.add(
      new (PointAttribute as any)('intensity', PointAttributeTypes.DATA_TYPE_UINT16, 1),
    );
    attributes.add(
      new (PointAttribute as any)('classification', PointAttributeTypes.DATA_TYPE_UINT8, 1),
    );
    attributes.add(new (PointAttribute as any)('gps-time', PointAttributeTypes.DATA_TYPE_FLOAT, 1));
    attributes.add(
      new (PointAttribute as any)('returnNumber', PointAttributeTypes.DATA_TYPE_UINT8, 1),
    );
    attributes.add(
      new (PointAttribute as any)('number of returns', PointAttributeTypes.DATA_TYPE_UINT8, 1),
    );
    attributes.add(
      new (PointAttribute as any)('return number', PointAttributeTypes.DATA_TYPE_UINT8, 1),
    );
    attributes.add(
      new (PointAttribute as any)('source id', PointAttributeTypes.DATA_TYPE_UINT16, 1),
    );
    this.pointAttributes = attributes;
  }
}

export class PointCloudCopcGeometry extends BaseGeometry {
  type = 'copc' as const;
  getter: any;
  copc: any;
  pages: Record<string, any>;

  static parse({ header, info, wkt }: { header: any; info: any; wkt?: string }) {
    return {
      cube: info.cube,
      boundsConforming: [...header.min, ...header.max],
      spacing: info.spacing,
      srs: wkt,
    };
  }

  constructor(getter: any, copc: any) {
    super(PointCloudCopcGeometry.parse(copc));
    this.getter = getter;
    this.copc = copc;
    this.pages = { '0-0-0-0': copc.info.rootHierarchyPage };
    this.loader = new CopcLaszipLoader();
  }

  async loadHierarchyPage(key: any): Promise<any> {
    const { Copc, Key } = CopcLib;
    const page = this.pages[Key.toString(key)];
    return Copc.loadHierarchyPage(this.getter, page);
  }
}

export class PointCloudEptGeometry extends BaseGeometry {
  type = 'ept' as const;
  base: string;
  ept: EptJson;

  static parse(ept: EptJson) {
    const { bounds: cube, boundsConforming, span, srs: filesrs } = ept;
    const spacing = (cube[3] - cube[0]) / span;

    let srs: string | undefined;
    if (filesrs) {
      const { authority, horizontal, wkt } = filesrs;
      if (authority && horizontal) {
        srs = `${authority}:${horizontal}`;
      }
      if (!srs && wkt) srs = wkt;
    }

    return { cube, boundsConforming, spacing, srs };
  }

  constructor(base: string, ept: EptJson) {
    super(PointCloudEptGeometry.parse(ept));
    this.base = base;
    this.ept = ept;

    switch (ept.dataType) {
      case 'laszip':
        this.loader = new EptLaszipLoader();
        break;
      case 'binary':
        this.loader = new EptBinaryLoader();
        break;
      case 'zstandard':
        this.loader = new EptZstandardLoader();
        break;
      default:
        throw new Error('Invalid data type: ' + ept.dataType);
    }
  }

  async loadHierarchyPage(key: any): Promise<any> {
    const { Ept, Key } = CopcLib;
    const filename = `${this.base}/ept-hierarchy/${Key.toString(key)}.json`;
    const response = await fetch(appendSasToken(filename));
    const json = await response.json();
    return Ept.Hierarchy.parse(json);
  }
}

// ─── Geometry Node ────────────────────────────────────────────────────────────

export class PointCloudEptGeometryNode extends (PointCloudTreeNode as any) {
  static IDCount = 0;

  owner: PointCloudCopcGeometry | PointCloudEptGeometry;
  key: any;
  bounds: number[];
  id: number;
  geometry: THREE.BufferGeometry | null;
  boundingBox: THREE.Box3;
  tightBoundingBox: THREE.Box3;
  spacing: number;
  boundingSphere: THREE.Sphere;
  hasChildren: boolean;
  children: Record<number, PointCloudEptGeometryNode>;
  nodeinfo: any;
  numPoints: number;
  level: number;
  loaded: boolean;
  loading: boolean;
  oneTimeDisposeHandlers: (() => void)[];
  name: string;
  index: number;
  parent?: PointCloudEptGeometryNode;
  mean?: THREE.Vector3;

  constructor(owner: PointCloudCopcGeometry | PointCloudEptGeometry, key?: any, bounds?: number[]) {
    super();

    const { Key } = CopcLib;

    this.owner = owner;
    this.key = key || Key.create(0, 0, 0, 0);
    this.bounds = bounds || owner.cube;

    this.id = PointCloudEptGeometryNode.IDCount++;
    this.geometry = null;
    this.boundingBox = U.toBox3(this.bounds);
    this.tightBoundingBox = this.boundingBox;
    this.spacing = this.owner.spacing / Math.pow(2, Key.depth(this.key));
    this.boundingSphere = U.sphereFrom(this.boundingBox);

    this.hasChildren = false;
    this.children = {};
    this.nodeinfo = undefined;
    this.numPoints = -1;

    this.level = Key.depth(this.key);
    this.loaded = false;
    this.loading = false;
    this.oneTimeDisposeHandlers = [];

    this.name = U.toPotreeName(this.key);
    this.index = parseInt(this.name.charAt(this.name.length - 1));
  }

  isGeometryNode(): boolean {
    return true;
  }
  getLevel(): number {
    return this.level;
  }
  isTreeNode(): boolean {
    return false;
  }
  isLoaded(): boolean {
    return this.loaded;
  }
  getBoundingSphere(): THREE.Sphere {
    return this.boundingSphere;
  }
  getBoundingBox(): THREE.Box3 {
    return this.boundingBox;
  }
  getNumPoints(): number {
    return this.nodeinfo ? this.nodeinfo.pointCount : -1;
  }

  getChildren(): PointCloudEptGeometryNode[] {
    const children: PointCloudEptGeometryNode[] = [];
    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i]);
      }
    }
    return children;
  }

  addChild(child: PointCloudEptGeometryNode): void {
    this.children[child.index] = child;
    child.parent = this;
  }

  async load(): Promise<void> {
    if (this.loaded || this.loading) return;
    if (getNumNodesLoading() >= getMaxNodesLoading()) return;

    this.loading = true;
    incrementNodesLoading();

    if (!this.nodeinfo) await this.loadHierarchy();
    this.loadPoints();
  }

  loadPoints(): void {
    this.owner.loader.load(this);
  }

  async loadHierarchy(): Promise<void> {
    const { Bounds, Key } = CopcLib;
    const ourkeyname = Key.toString(this.key);

    const nodemap: Record<string, PointCloudEptGeometryNode> = {};
    nodemap[ourkeyname] = this;
    this.hasChildren = false;

    const { nodes, pages } = await this.owner.loadHierarchyPage(this.key);

    const keys = Object.keys({ ...nodes, ...pages })
      .map(Key.create)
      .sort(Key.compare);

    keys.forEach((key: any) => {
      const keyname = Key.toString(key);
      if (keyname === ourkeyname) {
        this.nodeinfo = nodes[keyname];
        return;
      }

      const [_d, x, y, z] = key;
      const step: [number, number, number] = [x & 1, y & 1, z & 1];

      const parentName = Key.toString(Key.up(key));
      const parentNode = nodemap[parentName];
      if (!parentNode) return;
      parentNode.hasChildren = true;

      const bounds = Bounds.step(parentNode.bounds as any, step as any);
      const node = new PointCloudEptGeometryNode(this.owner, key, bounds);
      parentNode.addChild(node);
      nodemap[keyname] = node;

      const nodeinfo = nodes[keyname];
      if (nodeinfo) node.nodeinfo = nodeinfo;

      const pageinfo = pages[keyname];
      if ((this.owner as PointCloudCopcGeometry).pages && pageinfo) {
        (this.owner as PointCloudCopcGeometry).pages[keyname] = pageinfo;
      }
    });
  }

  doneLoading(
    bufferGeometry: THREE.BufferGeometry,
    tightBoundingBox: THREE.Box3,
    np: number,
    mean: THREE.Vector3,
  ): void {
    bufferGeometry.boundingBox = this.boundingBox;
    this.geometry = bufferGeometry;
    this.tightBoundingBox = tightBoundingBox;
    this.numPoints = np;
    this.mean = mean;
    this.loaded = true;
    this.loading = false;
    decrementNodesLoading();
  }

  dispose(): void {
    if (this.geometry && this.parent) {
      this.geometry.dispose();
      this.geometry = null;
      this.loaded = false;

      for (const handler of this.oneTimeDisposeHandlers) {
        handler();
      }
      this.oneTimeDisposeHandlers = [];
    }
  }
}

// Alias for backwards compatibility
export const PointCloudCopcGeometryNode = PointCloudEptGeometryNode;
